import React, { useRef, useState, useEffect, useCallback } from "react";
import Oauth from "../../utils/Oauth";
import SessionTimeoutModal from "./SessionTimoutModal.js";
import SessionConstants, { SESSION_EXPIRED_EVENT } from "../../config/SessionConstants.js";

// The visible countdown shown in the warning modal before the session ends.
const COUNTDOWN_DURATION = 5 * 60 * 1000; // 5 minutes

// Two non-renewable notices before the absolute 12h session cap: a 30-minute heads-up and a
// final 5-minute reminder. Unlike the token-expiry warning these cannot extend the session -
// the cap is a hard maximum - so they only tell the user the exact time they will be signed
// out and ask them to save their work.
const HARDCAP_WARN_FIRST_MS = 30 * 60 * 1000;  // 30 minutes before the cap
const HARDCAP_WARN_SECOND_MS = 5 * 60 * 1000;  // 5 minutes before the cap

// End the ED3N countdown this long before the recorded SAMS expiry. The stored expiry is
// computed a moment after SAMS actually issued the token, so it already runs slightly late;
// ending early guarantees the countdown reaches zero - and that a last-second "Continue"
// refresh still has time to reach SAMS - before the real access token dies.
const SAFETY_BUFFER = 60 * 1000; // 1 minute

const isDevLogin = () => sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true';

const SessionTimeout = () => {
    const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(0);
    const [restoreError, setRestoreError] = useState(false);
    const [continuing, setContinuing] = useState(false);
    //  'renew'  = ordinary token-expiry warning (user can click Continue to extend).
    //  'notice' = non-renewable absolute-cap reminder that just shows the sign-out time.
    const [modalMode, setModalMode] = useState('renew');
    const [noticeDeadline, setNoticeDeadline] = useState(null);
    const countdownEndTime = useRef(null);

    // Mirror of timeoutModalOpen so the monitor/session-expired callbacks never act on a
    // value captured from a stale render closure.
    const modalOpenRef = useRef(false);
    useEffect(() => {
        modalOpenRef.current = timeoutModalOpen;
    }, [timeoutModalOpen]);

    const clearCountdown = () => {
        countdownEndTime.current = null;
    };

    const handleLogout = useCallback(() => {
        modalOpenRef.current = false;
        setTimeoutModalOpen(false);
        clearCountdown();
        //  Capture the dev flag before clearing so performLogout can skip the SAMS logout for a
        //  dev login (which never authenticated through SAMS).
        const wasDevLogin = isDevLogin();
        Oauth.clearAllSessionVariables();

        sessionStorage.setItem(SessionConstants.LOGOUT_STATUS, true);
        sessionStorage.setItem(
            SessionConstants.ERROR_DESCRIPTION,
            "Logged out of ED3N"
        );
        Oauth.performLogout(wasDevLogin);
    }, []);

    //  Ends a session that expired on its own (idle/token deadline, absolute cap, or a
    //  rejected refresh) - as opposed to the user clicking Logout. Routes through
    //  Oauth.endExpiredSession so the landing page receives the timeout status and the
    //  "your login has expired" message (handleLogout records a generic manual logout), and
    //  so the SAMS SSO session is ended. endExpiredSession captures the dev flag, clears
    //  state, records the expiry status, and performs the logout/redirect.
    const handleExpire = useCallback(() => {
        modalOpenRef.current = false;
        setTimeoutModalOpen(false);
        clearCountdown();
        Oauth.endExpiredSession();
    }, []);

    // Open the ordinary warning: the session is nearing its end but can still be renewed by
    // clicking "Continue Session". endTime is when the countdown must reach zero (and the
    // auto-logout fires), already pulled in by the safety buffer for SAMS sessions.
    // modalOpenRef is set synchronously here so the monitor cannot re-open on a stale value
    // in the brief window before the mirroring effect runs.
    const openWarning = useCallback((endTime) => {
        modalOpenRef.current = true;
        countdownEndTime.current = endTime;
        setModalMode('renew');
        setRestoreError(false);
        setTimeoutModalOpen(true);
        setTimeoutCountdown(Math.max(0, Math.ceil((endTime - Date.now()) / 1000)));
    }, []);

    // Open a non-renewable absolute-cap notice. Unlike openWarning there is no countdown to
    // zero and no Continue button: the session ends at `deadline` no matter what, so this only
    // shows the exact sign-out time and lets the user acknowledge it. modalOpenRef is set
    // synchronously so the monitor cannot re-open on a stale value before the mirroring effect
    // runs.
    const openHardCapNotice = useCallback((deadline) => {
        modalOpenRef.current = true;
        setNoticeDeadline(deadline);
        setModalMode('notice');
        setRestoreError(false);
        setTimeoutModalOpen(true);
    }, []);

    // The user acknowledged the absolute-cap notice. Just close it - the deadline is unchanged,
    // so the monitor will surface the 5-minute notice and finally log the user out on its own.
    const handleAcknowledgeNotice = useCallback(() => {
        modalOpenRef.current = false;
        setTimeoutModalOpen(false);
        setModalMode('renew');
    }, []);

    const handleContinue = async () => {
        setContinuing(true);
        try {
            //  Enforce the absolute cap before renewing. The monitor also checks it, but the
            //  warning modal may have already been open when the cap passed, so re-check here
            //  so "Continue" can never refresh a session past its absolute maximum.
            if (Oauth.isSessionHardExpired()) {
                console.error('[SessionTimeout] Absolute session cap reached; cannot continue.');
                handleExpire();
                return;
            }
            if (isDevLogin()) {
                //  Dev login never talks to SAMS, so there is nothing to refresh: just
                //  start a fresh local session and close the warning. startDevSession refuses
                //  to revive a deadline that has already elapsed (e.g. the machine slept past
                //  expiry and Continue was clicked on resume), so end the session in that case.
                if (!Oauth.startDevSession()) {
                    console.error('[SessionTimeout] Dev session deadline already passed; ending session.');
                    handleExpire();
                    return;
                }
            } else {
                const result = await Oauth.refreshSamsToken();

                if (!result.ok) {
                    if (result.reason === 'rejected') {
                        //  The session is genuinely over and cannot be renewed, so end it
                        //  immediately - there is no grace period.
                        console.error('[SessionTimeout] SAMS refused to renew the session.');
                        handleExpire();
                    } else {
                        //  Could not reach SAMS. The session is probably fine, so keep the
                        //  action available; the countdown still guards a real expiry.
                        console.error('[SessionTimeout] Could not reach SAMS to renew the session.', result.reason);
                        setRestoreError(true);
                    }
                    return;
                }
            }

            //  Renewed. Close the warning; the monitor re-arms from the new expiry.
            modalOpenRef.current = false;
            setTimeoutModalOpen(false);
            setRestoreError(false);
            clearCountdown();
        } finally {
            setContinuing(false);
        }
    };

    // Single session monitor. The session length is now simply the SAMS token lifetime
    // (or a fixed 60 minutes for dev login): every second this compares the recorded expiry
    // against the clock and opens the warning once the session enters the countdown window.
    // An actively-navigating user is kept alive by the refresh in Oauth.isLoginValid, which
    // pushes the expiry out on every navigation, so this only fires when a user lingers on a
    // single page long enough to approach the token's expiry.
    useEffect(() => {
        const tick = () => {
            //  Absolute session cap. Enforce this FIRST - even while the warning modal is open -
            //  so a lingering session cannot be kept alive, or renewed via "Continue", past the
            //  absolute maximum. Once passed the session is over and cannot be renewed, so end
            //  it immediately; there is no grace period. This also enforces the cap when a user
            //  lingers on one page (never navigating) long enough to cross it.
            const hardExpiry = Number(sessionStorage.getItem(SessionConstants.SESSION_HARD_EXPIRY));
            if (hardExpiry && Date.now() >= hardExpiry) {
                handleExpire();
                return;
            }

            //  Idle/token deadline. Compute it up front and enforce a fully-passed deadline
            //  even while a modal is open. This must run BEFORE the modal guard below: a
            //  non-renewable absolute-cap notice does NOT cover the token dying, so if the
            //  access token expires while such a notice is on screen the session must still end
            //  immediately rather than lingering (unusable) until the absolute cap.
            //  Dev sessions have no real token to outrun, so they run the full duration; SAMS
            //  sessions end the countdown a safety buffer before the recorded expiry so the
            //  logout completes before the real access token dies.
            const expiresAt = Number(sessionStorage.getItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT));
            const logoutTime = expiresAt
                ? (isDevLogin() ? expiresAt : expiresAt - SAFETY_BUFFER)
                : null;
            if (logoutTime !== null && Date.now() >= logoutTime) {
                handleExpire();
                return;
            }

            //  A modal is already showing. A renewable warning drives its own countdown to the
            //  logoutTime enforced above, and a notice must not be stacked with another modal,
            //  so in either case stop here - the deadline checks above have already run.
            if (modalOpenRef.current) return;

            //  Absolute-cap pre-warnings. These are non-renewable notices (the cap cannot be
            //  pushed out) so each fires once: a 30-minute heads-up and a final 5-minute
            //  reminder. The "shown" flag stores the hard-expiry value it was shown for, so it
            //  persists across this component's per-navigation remounts yet re-arms
            //  automatically at the next login (which records a new hard-expiry). Showing the
            //  5-minute notice also marks the 30-minute tier, so a user who first opens the app
            //  already inside the final window does not get both notices back to back.
            if (hardExpiry) {
                const msUntilHardCap = hardExpiry - Date.now();
                if (msUntilHardCap <= HARDCAP_WARN_SECOND_MS) {
                    if (Number(sessionStorage.getItem(SessionConstants.HARDCAP_WARN_5_FOR)) !== hardExpiry) {
                        sessionStorage.setItem(SessionConstants.HARDCAP_WARN_5_FOR, String(hardExpiry));
                        sessionStorage.setItem(SessionConstants.HARDCAP_WARN_30_FOR, String(hardExpiry));
                        openHardCapNotice(hardExpiry);
                        return;
                    }
                } else if (msUntilHardCap <= HARDCAP_WARN_FIRST_MS) {
                    if (Number(sessionStorage.getItem(SessionConstants.HARDCAP_WARN_30_FOR)) !== hardExpiry) {
                        sessionStorage.setItem(SessionConstants.HARDCAP_WARN_30_FOR, String(hardExpiry));
                        openHardCapNotice(hardExpiry);
                        return;
                    }
                }
            }

            if (logoutTime === null) return;
            if (Date.now() >= logoutTime - COUNTDOWN_DURATION) {
                openWarning(logoutTime);
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [openWarning, openHardCapNotice, handleExpire]);

    // A request 401'd and the refresh was rejected outright, so the session is over. End it
    // immediately through the expiry path - there is no grace period.
    useEffect(() => {
        const onSessionExpired = () => {
            handleExpire();
        };

        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, [handleExpire]);

    // Countdown effect
    useEffect(() => {
        if (!timeoutModalOpen || !countdownEndTime.current) return;

        const interval = setInterval(() => {
        // Guard: if countdownEndTime was cleared (e.g., user clicked Continue), bail out
        if (!countdownEndTime.current) return;

        const remainingTime = Math.max(
            0,
            Math.floor((countdownEndTime.current - Date.now()) / 1000)
        );

        setTimeoutCountdown(remainingTime);

        if (remainingTime <= 0) {
            clearInterval(interval);
            handleExpire(); // Deadline reached: end the session via the expiry path
        }
        }, 1000);

        return () => clearInterval(interval); // Cleanup on modal close
    }, [timeoutModalOpen, handleExpire]);

    return (
        <SessionTimeoutModal
            mode={modalMode}
            countdown={timeoutCountdown}
            logoutTime={noticeDeadline}
            onContinue={handleContinue}
            onLogout={handleLogout}
            onAcknowledge={handleAcknowledgeNotice}
            open={timeoutModalOpen}
            restoreError={restoreError}
            busy={continuing}
        />
    );
};

export default SessionTimeout;
